import { SyntenyTypeEnum, TrackEnum } from "@selector/shared/openapi";
import { clamp } from "lodash-es";
import { colors } from "widgets/utils";

import { GROUP_HEADER_HEIGHT } from "../../settings.ts";
import { type CanvasState } from "../../store/slices/canvas-slice.ts";
import type { GenomeViewerState } from "../../store/store.ts";
import type {
  MainThreadHoverMessage,
  OrthologousGroup,
  SelectionRange,
  Track,
  TrackGroups,
  Tracks,
} from "../../types.ts";
import { renderAlignment } from "./alignment-renderer.ts";
import { drawHover } from "./hover-renderer.ts";
import { rangeSelectionBarRenderer } from "./range-selection-bar-renderer.ts";
import { rangeSelectionRenderer } from "./range-selection-renderer.ts";

/**
 * @param tracks all tracks
 * @param baseShift scrollX in base to prevent approximation of pixels
 */
export function drawTrackImageBitmaps(
  tracks: Track[],
  baseShift: number,
  ctx: CanvasRenderingContext2D,
  activeSyntenyType: SyntenyTypeEnum | undefined,
  basePerPixel: number,
  scrollX: number,
  scrollOffsetY: number,
) {
  let currentY = GROUP_HEADER_HEIGHT;

  if (!ctx) return;

  for (const { imageBitmap, height, baseStart, basePerPixel: trackBasePerPixel, trackType } of tracks) {
    const sliceEndY = currentY + height;
    ctx.save();
    if (
      scrollOffsetY + sliceEndY > 0 &&
      scrollOffsetY + currentY < ctx.canvas.height &&
      imageBitmap &&
      baseStart !== undefined &&
      trackBasePerPixel !== undefined
    ) {
      if (
        trackType === TrackEnum.SEQUENCE &&
        Math.floor(1 / trackBasePerPixel) < 1 &&
        activeSyntenyType != SyntenyTypeEnum.GENOME_ALIGNMENT
      ) {
        ctx.translate(0, currentY);
        // If not zoomed enough to see sequence track
        ctx.font = "12px Roboto Flex";
        ctx.fillStyle = colors.textSecondary;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Zoom in to see sequences", ctx.canvas.width / 2, height / 2);
      } else if (imageBitmap) {
        // Calculate the scale factor between the track's basePerPixel and the current state's basePerPixel
        const scaleFactor = trackBasePerPixel / basePerPixel;

        // Apply the scaling to both position and image
        const xPosition = (baseShift + baseStart) / basePerPixel - scrollX;
        ctx.translate(xPosition, currentY);

        ctx.scale(scaleFactor, 1); // Scale only horizontally
        // Draw the image bitmap for other track types
        ctx.drawImage(imageBitmap, 0, 0);
      }
    }

    ctx.restore();

    currentY = sliceEndY;
  }
}

export function drawAlignmentImageBitmaps(
  ctx: CanvasRenderingContext2D | null,
  alignmentImageBitmapBasePerPixel: number,
  alignmentImageBitmap: ImageBitmap | undefined,
  alignmentImageBitmapScrollX: number,
  alignmentImageBitmapScrollY: number,
  basePerPixel: number,
  scrollX: number,
  scrollY: number,
) {
  if (!ctx) return;

  const scaleFactor = alignmentImageBitmapBasePerPixel / basePerPixel;

  if (alignmentImageBitmap) {
    ctx.save();

    const xPosition = scaleFactor * alignmentImageBitmapScrollX - scrollX;

    ctx.translate(xPosition, alignmentImageBitmapScrollY - scrollY);
    ctx.scale(scaleFactor, 1); // Scale only horizontally
    ctx.drawImage(alignmentImageBitmap, 0, 0);
    ctx.restore();
  }
}

export function drawTrackShiftBackground(
  shiftX: number,
  height: number,
  length: number,
  ctx: CanvasRenderingContext2D,
  scrollX: number,
  basePerPixel: number,
) {
  // At the beginning of the track
  if (scrollX < shiftX) {
    ctx.save();
    ctx.fillStyle = colors.disabledBackground;
    ctx.fillRect(0, 0, shiftX - scrollX, height);
    ctx.restore();
  }

  // At the end of the track
  if (scrollX + ctx.canvas.width > shiftX + length / basePerPixel) {
    ctx.save();
    ctx.fillStyle = colors.disabledBackground;
    ctx.fillRect(
      scrollX > shiftX + length / basePerPixel ? 0 : shiftX + length / basePerPixel - scrollX,
      0,
      ctx.canvas.width,
      height,
    );
    ctx.restore();
  }
}

export function drawHoveredFeatures(
  ctx: CanvasRenderingContext2D | null,
  isDragging: boolean,
  hoverData: MainThreadHoverMessage,
  orthologousGroups: OrthologousGroup[],
  tracks: Tracks,
  trackGroups: TrackGroups,
  scrollX: number,
  scrollY: number,
  basePerPixel: number,
) {
  if (!isDragging && hoverData != undefined) {
    // find orthologs friends
    const friends = (
      orthologousGroups.find((group) => group.orthologId === hoverData?.orthologId)?.orthologs ?? []
    ).filter((f) => f.id !== hoverData?.id);

    for (const friend of friends) {
      const track = tracks.get(friend.trackId);
      if (track?.isVisible) {
        const trackGroup = trackGroups.get(track.groupId);
        if (trackGroup?.graphics.isVisible) {
          drawHover(
            { ...friend, x: friend.x - scrollX + trackGroup.graphics.baseShift / basePerPixel },
            ctx,
            scrollY,
            trackGroups,
            tracks,
            true,
          );
        }
      }
    }
    drawHover(hoverData, ctx, scrollY, trackGroups, tracks);
  }
}

export function drawRangeSelectionText(x: number, text: string, ctx: CanvasRenderingContext2D) {
  if (ctx && text) {
    const textWidth = ctx.measureText(text).width;

    ctx.fillStyle = colors.panelBackground;
    ctx.fillRect(x - textWidth / 2 - 4, 1, textWidth + 8, 11 + 4); // Because 11px font and +4 for vertical padding

    ctx.fillStyle = colors.selectStroke;
    ctx.fillText(text, x - textWidth / 2, GROUP_HEADER_HEIGHT / 2 + 3);
  }
}

type DrawAlignmentProps = Pick<
  GenomeViewerState,
  "tracks" | "trackGroups" | "alignedFamily" | "alignedTrackGroupStarts"
> &
  CanvasState & {
    ctx: CanvasRenderingContext2D;
  };

export const drawAlignment = ({
  ctx,
  trackGroups,
  tracks,
  basePerPixel,
  scrollX,
  scrollY,
  alignedFamily,
  alignedTrackGroupStarts,
}: DrawAlignmentProps) => {
  if (alignedFamily === undefined) {
    return;
  }
  if (!alignedTrackGroupStarts) {
    return;
  }

  let offsetY = 0;
  for (const [groupId, trackGroup] of trackGroups.entries()) {
    if (!trackGroup.graphics.isVisible) {
      continue;
    }
    const visibleTracks = trackGroup.trackIds
      .map((trackId) => tracks.get(trackId))
      .filter((track): track is NonNullable<typeof track> => !!track?.isVisible);
    const height = visibleTracks.reduce((acc, track) => acc + track.height, GROUP_HEADER_HEIGHT);
    const alignedMember = alignedFamily.members.find((member) => trackGroup.trackIds.includes(member.trackId));
    const start = alignedMember?.start ?? alignedTrackGroupStarts.get(groupId);
    const shouldDraw = start != undefined;

    if (shouldDraw) {
      ctx.save();
      ctx.translate(0, Math.floor(offsetY - scrollY));
      renderAlignment(height, ctx, trackGroup, start!, basePerPixel, scrollX);
      ctx.restore();
    }
    offsetY += height;
  }
};

export function drawRangeSelection(
  ctx: CanvasRenderingContext2D,
  selectedRange: SelectionRange,
  trackGroups: TrackGroups,
  tracks: Tracks,
  trackGroupsInSelection: string[],
  basePerPixel: number,
  scrollX: number,
  scrollY: number,
  leftSelectionHovering: boolean,
  rightSelectionHovering: boolean,
) {
  if (selectedRange) {
    // Not `RobotoMono` in the main thread as we use the application web font
    ctx.font = "11px Roboto Flex Mono";

    const rangeStart = Math.min(selectedRange.start, selectedRange.stop);
    const rangeStop = Math.max(selectedRange.start, selectedRange.stop);

    const rangeLeftX = rangeStart / basePerPixel - scrollX;
    const rangeRightX = rangeStop / basePerPixel - scrollX;

    let offsetY = 0;

    // Draw the selected range relative coordinates
    for (const [groupId, trackGroup] of [...trackGroups.entries()].filter(([_, group]) => group.graphics.isVisible)) {
      const visibleTracks = trackGroup.trackIds
        .map((trackId) => tracks.get(trackId))
        .filter((track): track is NonNullable<typeof track> => !!track?.isVisible);

      ctx.save();
      ctx.translate(0, Math.floor(offsetY - scrollY));
      const height = [...visibleTracks.values()].reduce((acc, track) => acc + track.height, GROUP_HEADER_HEIGHT);

      if (trackGroupsInSelection.includes(groupId)) {
        rangeSelectionRenderer(height, ctx, selectedRange, basePerPixel, scrollX);
      }

      offsetY += height;
      ctx.restore();
    }

    // Draw range selection drag bars
    rangeSelectionBarRenderer(ctx, selectedRange, basePerPixel, scrollX, leftSelectionHovering, rightSelectionHovering);

    offsetY = 0;
    for (const [groupId, trackGroup] of [...trackGroups.entries()].filter(([_, group]) => group.graphics.isVisible)) {
      const visibleTracks = trackGroup.trackIds
        .map((trackId) => tracks.get(trackId))
        .filter((track): track is NonNullable<typeof track> => !!track?.isVisible);

      ctx.save();
      ctx.translate(0, Math.floor(offsetY - scrollY));
      const height = [...visibleTracks.values()].reduce((acc, track) => acc + track.height, GROUP_HEADER_HEIGHT);

      if (trackGroupsInSelection.includes(groupId))
        if (offsetY - scrollY + height > 0 && offsetY - scrollY < ctx.canvas.height) {
          const textLeftX = clamp(
            Math.floor(rangeStart - trackGroup.graphics.baseShift),
            0,
            trackGroup.graphics.length,
          );
          const textRightX = clamp(
            Math.floor(rangeStop - trackGroup.graphics.baseShift),
            0,
            trackGroup.graphics.length,
          );

          const textLength =
            textLeftX === textRightX ? "No data" : Math.abs(textRightX - textLeftX).toLocaleString("en-US") + " bp";

          const textLengthX = (clamp(rangeLeftX, 0, ctx.canvas.width) + clamp(rangeRightX, 0, ctx.canvas.width)) / 2;

          const canDisplayCoordinates =
            // + 12 to set padding between texts
            // Ensure that the length text isn't overlapping with left coordinates
            textLengthX - ctx.measureText(textLength).width / 2 - 6 >
              rangeLeftX + ctx.measureText(textLeftX.toLocaleString("en-US")).width / 2 + 6 &&
            // Ensure that the length text isn't overlapping with right coordinates
            textLengthX + ctx.measureText(textLength).width / 2 + 6 <
              rangeRightX - ctx.measureText(textLeftX.toLocaleString("en-US")).width / 2 - 6;

          const canDisplayLength =
            (rangeLeftX >= 0 && rangeLeftX <= ctx.canvas.width) ||
            (rangeRightX >= 0 && rangeRightX <= ctx.canvas.width) ||
            (scrollX >= rangeStart / basePerPixel && scrollX + ctx.canvas.width < rangeStop / basePerPixel);

          if (canDisplayCoordinates && canDisplayLength && textLeftX !== textRightX) {
            // Left coordinate of the range, relatively to the current trackGroup
            drawRangeSelectionText(rangeLeftX, (textLeftX + 1).toLocaleString("en-US"), ctx);
            // Right coordinate of the range, relatively to the current trackGroup
            drawRangeSelectionText(rangeRightX, (textRightX || 1).toLocaleString("en-US"), ctx);
          }

          if (canDisplayLength) {
            drawRangeSelectionText(textLengthX, textLength, ctx);
          }
        }

      offsetY += height;
      ctx.restore();
    }
  }
}

export function drawDarkOverlay(y: number, height: number, ctx: CanvasRenderingContext2D) {
  ctx.save();
  ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
  ctx.fillRect(0, y, ctx.canvas.width, y + height);
  ctx.restore();
}
